export const EMBEDDED_CONTEXT_STORAGE_KEY = "taas_embedded_context";

export type TaasEmbeddedClientContext = {
  device_type?: string;
  device_id?: string;
  version?: string;
  fcm_token?: string;
  /** Official Flutter WebView should set this to `"flutter"` when injecting bridge context. */
  embed_source?: string;
};

declare global {
  interface Window {
    __TAAS_EMBEDDED_CONTEXT__?: TaasEmbeddedClientContext;
  }
}

function readStoredContext(): TaasEmbeddedClientContext | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(EMBEDDED_CONTEXT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object") {
      return parsed as TaasEmbeddedClientContext;
    }
  } catch {
    // ignore invalid JSON
  }
  return null;
}

/** Merged embedded bridge context (localStorage then `window`; window wins). */
export function getTaasEmbeddedContext(): TaasEmbeddedClientContext {
  const fromStorage = readStoredContext();
  const fromWindow =
    typeof window !== "undefined" ? window.__TAAS_EMBEDDED_CONTEXT__ : null;
  return {
    ...(fromStorage || {}),
    ...(fromWindow || {}),
  };
}

function normalizeDeviceType(ctx: TaasEmbeddedClientContext): string | null {
  const raw = ctx.device_type;
  if (!raw || typeof raw !== "string") return null;
  const v = raw.trim().toLowerCase();
  return v || null;
}

/** Host explicitly reports the Flutter shell (`embed_source: "flutter"`). */
export function isFlutterEmbedSource(ctx?: TaasEmbeddedClientContext): boolean {
  const c = ctx ?? getTaasEmbeddedContext();
  const src = String(c.embed_source ?? "")
    .trim()
    .toLowerCase();
  return src === "flutter";
}

/**
 * True when `embed_source` is unset (legacy bridge) or explicitly `"flutter"`.
 * Use this to avoid hiding shell UX when older WebViews only set `device_type`.
 */
export function isFlutterEmbedSourceOrUnset(
  ctx?: TaasEmbeddedClientContext
): boolean {
  const c = ctx ?? getTaasEmbeddedContext();
  const raw = String(c.embed_source ?? "").trim();
  if (!raw) return true;
  return raw.toLowerCase() === "flutter";
}

/**
 * Flutter-embedded shell: bottom nav, compact inbox, landing tweaks, etc.
 * Requires `sm` viewport, `device_type` android/ios, and either missing `embed_source`
 * (legacy Flutter / bridge) or `embed_source: "flutter"`. Any other non-empty
 * `embed_source` turns the shell off so plain mobile web can opt out later.
 */
export function isFlutterEmbeddedMobileViewport(isSmDown: boolean): boolean {
  if (!isSmDown) return false;
  const ctx = getTaasEmbeddedContext();
  if (!isFlutterEmbedSourceOrUnset(ctx)) return false;
  const dt = normalizeDeviceType(ctx);
  return dt === "android" || dt === "ios";
}

/** Fired when user taps Purchase / Redeem chips in the Flutter embedded wallet shell. */
export type TaasWalletFlowClickPayload = {
  flow: "purchase" | "redeem";
  /** Same URL shown in “Open in browser” (e.g. `/wallet?flow=purchase&embed=flutter`). */
  url: string;
  context: TaasEmbeddedClientContext;
};

type WindowWithExternalOpen = Window &
  typeof globalThis & {
    TaasHostOpenUrl?: (url: string) => void;
    TaasHostWalletFlowClicked?: (payload: TaasWalletFlowClickPayload) => void;
    flutter_inappwebview?: {
      callHandler: (name: string, ...args: unknown[]) => Promise<unknown>;
    };
  };

/**
 * Ask the native host (Flutter WebView) to open a URL in the **system browser**.
 * `window.open(..., "_blank")` usually stays inside the WebView — avoid that here.
 *
 * Host should implement **one** of:
 * - `window.TaasHostOpenUrl(url: string)`
 * - `window.flutter_inappwebview.callHandler("openExternalUrl", url)` (flutter_inappwebview)
 * - Listen for `window` message `{ type: "taas_open_external_url", url }` or `CustomEvent("taas:open-external-url", { detail: { url } })`
 */
export function openExternalUrlFromEmbeddedHost(url: string): void {
  if (typeof window === "undefined" || !url) return;
  const w = window as WindowWithExternalOpen;

  if (typeof w.TaasHostOpenUrl === "function") {
    try {
      w.TaasHostOpenUrl(url);
      return;
    } catch {
      // fall through
    }
  }

  if (w.flutter_inappwebview?.callHandler) {
    void w.flutter_inappwebview
      .callHandler("openExternalUrl", url)
      .catch(() => {
        /* host may not implement handler */
      });
    return;
  }

  try {
    window.dispatchEvent(
      new CustomEvent("taas:open-external-url", { detail: { url } }),
    );
  } catch {
    // ignore
  }
  try {
    window.postMessage({ type: "taas_open_external_url", url }, "*");
  } catch {
    // ignore
  }
}

/**
 * Tell the native host that the user tapped **Purchase chips** or **Redeem chips**
 * in the embedded wallet (before “Open in browser”).
 *
 * Host can listen for **one** of:
 * - `window.TaasHostWalletFlowClicked(payload)`
 * - `window.flutter_inappwebview.callHandler("walletFlowClicked", payload)`
 * - `CustomEvent("taas:wallet-flow-clicked", { detail: payload })`
 * - `postMessage` with `{ type: "taas_wallet_flow_clicked", flow, url, context }`
 */
export function notifyEmbeddedHostWalletFlowClicked(
  args: Pick<TaasWalletFlowClickPayload, "flow" | "url">,
): void {
  if (typeof window === "undefined") return;
  const payload: TaasWalletFlowClickPayload = {
    ...args,
    context: getTaasEmbeddedContext(),
  };
  const w = window as WindowWithExternalOpen;

  if (typeof w.TaasHostWalletFlowClicked === "function") {
    try {
      w.TaasHostWalletFlowClicked(payload);
      return;
    } catch {
      // fall through
    }
  }

  if (w.flutter_inappwebview?.callHandler) {
    void w.flutter_inappwebview
      .callHandler("walletFlowClicked", payload)
      .catch(() => {
        /* host may not implement handler */
      });
    return;
  }

  try {
    window.dispatchEvent(
      new CustomEvent("taas:wallet-flow-clicked", { detail: payload }),
    );
  } catch {
    // ignore
  }
  try {
    window.postMessage(
      { type: "taas_wallet_flow_clicked", ...payload },
      "*",
    );
  } catch {
    // ignore
  }
}
